import * as React from 'react'
import * as PropTypes from 'prop-types'
import styled from 'styled-components'
import { Link } from 'react-router-dom'
import { Fade } from 'react-reveal'

import LoadingBase from '../../loading/LoadingBase/LoadingBase'

import media from '../../../services/generateMediaQueries'

import ZingIcon from '../../../assets/img/zing-icon.svg'

let LOADING = false

const StyledLink = styled(Link)`
  text-decoration: none;
`

const CardWrapper = styled.div`
  outline: none;
  ${media.lessThan('small')`
    width: 100%;
  `}
`

const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: 1.5rem;
  height: 100%;
  border-radius: var(--border-radius-card);
  color: #ffffff;
  text-align: center;
  z-index: 1;
  place-content: center;

  ${media.lessThan('small')`
    flex-direction: row;
    color: unset;
    padding: 0;
    justify-content: unset;
  `}
`

const CardImageWrapper = styled.div`
  ${media.lessThan('small')`
    display: flex;
    justify-content: center;
    flex: 0 0 auto;
    position: relative;
    height: 94px;
    width: 94px;
    border-radius: 5px;
    overflow: hidden;
  `}
`

const CardImage = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
  left: 0;
  background-color: #000000;
  background: linear-gradient(rgba(141,122,157,0.40), rgba(141,122,157,0.40)), url('${props =>
    props.cardImageUrl}') 0% 0% / cover, linear-gradient(#000000, #000000);
  background-size: cover;
  background-position: center;
  transition: all .5s;
  z-index: -1;
`

const StyledCard = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 244px;
  height: 244px;
  margin: 0 0.625rem;
  border-radius: var(--border-radius-card);
  box-shadow: var(--box-shadow-large);
  overflow: hidden;
  background: linear-gradient(
      rgba(141, 122, 157, 0.4),
      rgba(141, 122, 157, 0.4)
    ),
    #f9f9f9;

  ${media.lessThan('small')`
    height: 94px;
    width: 100%;
    border-radius: 5px;;
    background: #ffffff;
    margin-left: 0 !important;
    margin-right: 0 !important;
  `}

  ${media.lessThan('large')`
    margin: 0 0.5rem;
  `}

  &:hover {
    ${CardImage} {
      transform: scale(1.2);
    }
  }
`

const LogoWrapper = styled.div`
  padding: 1rem;

  ${media.lessThan('small')`
    padding: 1rem;
  `}
`

const Logo = styled.div.attrs(props => ({
  style: {
    backgroundImage: `url('${props.image}')`,
    backgroundSize: 'contain',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
  },
}))`
  border-radius: 0.3125rem;
  height: 3.6875rem;
  width: 3.6875rem;
  margin: 1rem auto;

  ${media.lessThan('small')`
    margin: 0;
  `}
`

const CardTextContent = styled.div`
  ${media.lessThan('small')`
    padding: 1.25rem 1.125rem;
    text-align: left;
  `}
`

const Title = styled.h1`
  font-size: 1.25rem;
  font-weight: var(--font-weight-boldest);
  line-height: 1.5rem;
  letter-spacing: 0.28px;
  letter-spacing: 0.28px;
  margin: 0;
  margin-bottom: 0.5rem;
  text-transform: uppercase;

  ${media.lessThan('small')`
    font-size: 0.875rem;
    font-weight: var(--font-weight-bold);
    line-height: 1.1875rem;
    letter-spacing: 0.28px;
    color: #000000;
    text-transform: none;
  `}
`

const Subtitle = styled.p`
  font-size: 0.875rem;
  line-height: 1.1875rem;
  letter-spacing: 0.29px;
  margin: 0;
  display: ${props => (props.isOrder ? 'none' : '-webkit-box')};
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;

  ${media.lessThan('small')`
    font-size: 0.75rem;
    line-height: 1.125rem;
    letter-spacing: 0.24px;
    color: #9B9B9B;
    ${props => props.isOrder && 'display: -webkit-box'};
  `}
`

const Text = styled.p`
  margin: 0;
  font-weight: 500;
  text-align: center;

  ${media.greaterThan('small')`
    color: #ffffff;
  `}
`

const ZingIconComponent = styled.img.attrs({ src: ZingIcon })`
  margin-bottom: 1rem;

  ${media.lessThan('small')`
    flex: 0 0 auto;
    padding: 1.25rem;
    margin-bottom: 0;
  `}
`

const ErrorWrapper = styled.div`
  ${media.lessThan('small')`
    flex: 1 1 auto;
    padding: 1.25rem;
    text-align: left;
  `}
`

const LoadingWrapper = styled.div`
  flex: 1 1 auto;
  display: none;
  padding: 1rem;
  text-align: left;

  ${media.lessThan('small')`
    display: block;
  `}
`

const Loading = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;
  width: 100%;

  ${media.lessThan('small')`
    flex-direction: row;
    justify-content: unset;
  `}
`

function ShopDineListCard(props) {
  const {
    cardImageUrl = '',
    iconUrl = '',
    title = '',
    subtitle = '',
    link = '',
    onClick,
    onRetry,
    loading = false,
    error = false,
    errorMessage = '',
    id,
    isOrder,
    ...restProps
  } = props

  LOADING = loading

  const CardComponent = () => (
    <CardWrapper>
      <StyledLink
        to={link}
        onClick={
          loading
            ? e => {
                e.preventDefault()
                if (error) {
                  onRetry()
                }
              }
            : onClick
        }
      >
        <StyledCard {...restProps}>
          <CardContent>
            {!LOADING ? (
              <>
                <CardImageWrapper>
                  <CardImage cardImageUrl={cardImageUrl} />
                  <LogoWrapper>
                    <Logo image={iconUrl} />
                  </LogoWrapper>
                </CardImageWrapper>
                <CardTextContent>
                  <Title>{title}</Title>
                  <Subtitle isOrder={isOrder}>{subtitle}</Subtitle>
                </CardTextContent>
              </>
            ) : (
              <Loading>
                <ZingIconComponent />
                {error ? (
                  <ErrorWrapper>
                    <Text>{errorMessage || 'Failed to load Explore.'}</Text>
                    <Text>Tap to retry.</Text>
                  </ErrorWrapper>
                ) : (
                  <LoadingWrapper>
                    <LoadingBase size="small" lines={1} />
                  </LoadingWrapper>
                )}
              </Loading>
            )}
          </CardContent>
        </StyledCard>
      </StyledLink>
    </CardWrapper>
  )

  if (LOADING) {
    return <CardComponent />
  }

  return (
    <Fade right distance="1rem" delay={300 + 50 * id} duration={300} force>
      <CardComponent />
    </Fade>
  )
}

ShopDineListCard.defaultProps = {
  link: '',
}

ShopDineListCard.propTypes = {
  iconUrl: PropTypes.string,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  link: PropTypes.string,
  onClick: PropTypes.func,
  onRetry: PropTypes.func,
  loading: PropTypes.bool,
  error: PropTypes.bool,
  errorMessage: PropTypes.string,
}

export default ShopDineListCard
